import { Badge } from '@/components/ui/Badge';
import { useMemo } from 'react';
import {
  WorkspaceMetricCell,
  WorkspaceMetricHeaderCells,
  WorkspaceMetricTotalsRow,
} from '@/components/workspace/WorkspaceMetricCell';
import { WorkspaceColumnHeader } from '@/components/workspace/WorkspaceColumnHeader';
import {
  workspaceColumnStickyProps,
  workspaceDraggedColumnClass,
  workspaceDropTargetClass,
} from '@/components/workspace/WorkspaceOrderedHeaders';
import { useWorkspaceFrozenLayout } from '@/components/workspace/workspaceStickyColumns';
import { totalsFromMetricRows } from '@/utils/workspaceAdProductMetrics';
import {
  WorkspaceMiddleCell,
  WorkspaceSection,
  WorkspaceTableShell,
  tableRowClass,
  workspaceTableClass,
} from '@/components/workspace/WorkspaceSection';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { WorkspaceExportButton } from '@/components/workspace/WorkspaceExportButton';
import { useColumnOrder, WORKSPACE_LABEL_LEADING_FREEZE_IDS } from '@/components/workspace/useColumnOrder';
import { metricColumnPickerOptions } from '@/components/workspace/workspaceColumnConfig';
import { useTableSort } from '@/components/workspace/useTableSort';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { exportMetricRollupCsv } from '@/api/workspaceCsvExport';
import { useToast } from '@/components/ui/Toast';
import { WorkspaceMetricFiltersPopover } from '@/components/workspace/WorkspaceMetricFiltersPopover';
import { useMetricFilters } from '@/components/workspace/useMetricFilters';
import { rowMatchesMetricFilters } from '@/utils/workspaceMetricFilters';
import {
  AUDIENCE_METRIC_COLUMNS,
  AUDIENCE_SEGMENT_ROWS,
  EMPTY_AUDIENCE_METRICS,
  type AudienceDerivedMetrics,
  type AudienceSegmentId,
} from '@/utils/workspaceAudienceMetrics';

export function WorkspaceAudienceMetricsTable({
  current,
  previous,
  compareEnabled,
  isLoading,
  rangeSubtitle,
  isFiltered,
}: {
  current: Map<AudienceSegmentId, AudienceDerivedMetrics>;
  previous: Map<AudienceSegmentId, AudienceDerivedMetrics>;
  compareEnabled: boolean;
  isLoading: boolean;
  rangeSubtitle: string;
  isFiltered: boolean;
}) {
  const { showToast } = useToast();
  const { range } = useAppDateRange();
  const exportStart = range.current.startDate;
  const exportEnd = range.current.endDate;
  const tableSort = useTableSort({ defaultSortBy: 'cost', defaultSortDir: 'desc' });
  const metricFilters = useMetricFilters({
    tableId: 'audience-metrics',
    metrics: AUDIENCE_METRIC_COLUMNS,
  });
  const columnOrder = useColumnOrder(
    AUDIENCE_METRIC_COLUMNS.map((column) => column.key),
    undefined,
    WORKSPACE_LABEL_LEADING_FREEZE_IDS
  );
  const freezeLayout = useWorkspaceFrozenLayout(
    columnOrder.frozenColumnIds,
    columnOrder.displayOrder.join('\0')
  );
  const subtitleParts = [rangeSubtitle];
  if (isFiltered) subtitleParts.push('Filtered to selected entities');

  const rows = useMemo(
    () =>
      AUDIENCE_SEGMENT_ROWS.map(({ id, label }) => ({
        id,
        label,
        metrics: current.get(id) ?? EMPTY_AUDIENCE_METRICS,
        prevMetrics: previous.get(id) ?? EMPTY_AUDIENCE_METRICS,
      })).filter((row) => rowMatchesMetricFilters(row.metrics, metricFilters.completeFilters))
      .sort((left, right) => {
        if (tableSort.sortBy === 'label') {
          const cmp = left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
          return tableSort.sortDir === 'asc' ? cmp : -cmp;
        }
        const cmp =
          (left.metrics as Record<string, number>)[tableSort.sortBy] -
          (right.metrics as Record<string, number>)[tableSort.sortBy];
        return tableSort.sortDir === 'asc' ? cmp : -cmp;
      }),
    [current, previous, metricFilters.completeFilters, tableSort.sortBy, tableSort.sortDir]
  );

  const { currentTotals, previousTotals } = useMemo(() => {
    const currentRows = rows.map((row) => row.metrics);
    const previousRows = rows.map((row) => row.prevMetrics);
    const withUnits = (metricRows: typeof currentRows, base: ReturnType<typeof totalsFromMetricRows>) => ({
      ...base,
      units: metricRows.reduce((sum, row) => sum + (row.units ?? 0), 0),
    });
    return {
      currentTotals: withUnits(currentRows, totalsFromMetricRows(currentRows)),
      previousTotals: withUnits(previousRows, totalsFromMetricRows(previousRows)),
    };
  }, [rows]);

  return (
    <WorkspaceSection
      title="Audience Performance"
      countLabel={subtitleParts.join(' · ')}
      actions={
        <>
          <WorkspaceMetricFiltersPopover metrics={AUDIENCE_METRIC_COLUMNS} {...metricFilters} />
          <WorkspaceExportButton
            onExport={() => {
              exportMetricRollupCsv({
                filenamePrefix: 'workspace_audience_metrics',
                startDate: exportStart,
                endDate: exportEnd,
                labelHeader: 'Audience',
                visibleMetricIds: columnOrder.visibleIds,
                metricColumns: AUDIENCE_METRIC_COLUMNS,
                rows,
              });
              showToast(`Exported ${rows.length} rows`, 'success');
            }}
          />
          <ReportColumnPicker
            columns={metricColumnPickerOptions(AUDIENCE_METRIC_COLUMNS)}
            selected={columnOrder.visibleIds}
            onChange={columnOrder.setVisibleIds}
            panelTitle="Visible columns"
          />
        </>
      }
    >
      <WorkspaceTableShell
        isEmpty={rows.length === 0}
        emptyMessage="Try adjusting metric filters, or clear them to see all audience segments."
        isLoading={isLoading}
      >
        <table ref={freezeLayout.tableRef} className={workspaceTableClass.table}>
          <thead>
            <tr>
              <WorkspaceColumnHeader
                label="Audience / Segment"
                columnId="label"
                sortBy={tableSort.sortBy}
                sortDir={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                movable={false}
                isFrozen={false}
                className={workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById, 'header').className}
                style={workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById, 'header').style}
              />
              <WorkspaceMetricHeaderCells
                columns={columnOrder.displayOrder
                  .map((columnId) => AUDIENCE_METRIC_COLUMNS.find((item) => item.key === columnId))
                  .filter((item): item is (typeof AUDIENCE_METRIC_COLUMNS)[number] => Boolean(item))}
                sortable
                movable
                sortBy={tableSort.sortBy}
                sortDir={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                onFreeze={columnOrder.freezeColumn}
                frozenColumnIds={columnOrder.frozenColumnIds}
                pinnedFrozenColumnId={columnOrder.pinnedFrozenColumnId}
                frozenLeftById={freezeLayout.frozenLeftById}
                onDragStartColumn={columnOrder.startDrag}
                onDragHoverColumn={columnOrder.hoverReorder}
                onDropColumn={columnOrder.commitDrop}
                onDragEndColumn={columnOrder.endDrag}
                activeDragColumnId={columnOrder.activeDragColumnId}
                hoverColumnId={columnOrder.hoverColumnId}
                hoverPosition={columnOrder.hoverPosition}
                hoverInsertIndex={columnOrder.hoverInsertIndex}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(({ id, label, metrics, prevMetrics }) => {
              return (
                <tr key={id} className={tableRowClass(false)}>
                  <WorkspaceMiddleCell
                    className={`${workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById).className} ${columnOrder.activeDragColumnId === 'label' ? workspaceDraggedColumnClass(true) : ''}`}
                    style={workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById).style}
                  >
                    <Badge variant="neutral">{label}</Badge>
                  </WorkspaceMiddleCell>
                  {columnOrder.displayOrder.map((columnId) => {
                    const def = AUDIENCE_METRIC_COLUMNS.find((item) => item.key === columnId);
                    if (!def) return null;
                    const isDragged = columnOrder.activeDragColumnId === columnId;
                    const isDropTarget =
                      columnOrder.hoverColumnId === columnId &&
                      columnOrder.activeDragColumnId !== columnId;
                    const sticky = workspaceColumnStickyProps(
                      columnId,
                      columnOrder.frozenColumnIds,
                      freezeLayout.frozenLeftById
                    );
                    return (
                    <WorkspaceMetricCell
                      key={def.key}
                      def={def}
                      current={metrics[def.key]}
                      previous={prevMetrics[def.key]}
                      compareEnabled={compareEnabled}
                      isLoading={isLoading}
                      className={`${sticky.className} ${workspaceDraggedColumnClass(isDragged)} ${workspaceDropTargetClass(isDropTarget, columnOrder.hoverPosition)}`}
                      style={sticky.style}
                    />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <WorkspaceMetricTotalsRow
              labelColSpan={1}
              frozenColumnIds={columnOrder.frozenColumnIds}
              frozenLeftById={freezeLayout.frozenLeftById}
              current={currentTotals}
              previous={previousTotals}
              compareEnabled={compareEnabled}
              isLoading={isLoading}
              columns={columnOrder.displayOrder
                .map((columnId) => AUDIENCE_METRIC_COLUMNS.find((item) => item.key === columnId))
                .filter((item): item is (typeof AUDIENCE_METRIC_COLUMNS)[number] => Boolean(item))}
            />
          </tfoot>
        </table>
      </WorkspaceTableShell>
    </WorkspaceSection>
  );
}
